// Pure helpers for the unified chart context menu (feature flag `chartContextMenu`).
//
// One right-click / long-press on the chart canvas fans out to SEVERAL
// subscribers of the same ChartCanvas event ("contextmenu" broadcast):
// the background <Chart>, the DrawingObjectSelector (per section) and any
// hovered indicator series. They must collapse into ONE menu showing every
// applicable section. `mergeContextPayload` keys the merge on native-event
// identity (same dedup mechanism InteractiveContextMenu uses — the first
// notification wins per slot, duplicates from other sections are ignored).
//
// `normalizeShowEvent` reproduces the coordinate handling proven in
// InteractiveContextMenu: native mouse events pass through; touch events read
// changedTouches/touches; the synthetic long-press event from EventCapture
// (spread of a TouchEvent — loses every prototype getter, so no pageX, no
// target, no touches) falls back to moreProps.mouseXY + the chart container
// rect. Extracted here so both paths are unit-testable without a browser.

export const getNativeEvent = (event) => event?.nativeEvent || event;

const isTouchLike = (nativeEvent) =>
  Boolean(
    nativeEvent?.type?.startsWith?.("touch") ||
      nativeEvent?.touches ||
      nativeEvent?.changedTouches,
  );

/**
 * Build a `{ pageX, pageY, clientX, clientY, preventDefault, stopPropagation }`
 * object suitable for PrimeReact ContextMenu.show(), or null when no
 * coordinates can be derived.
 *
 * @param {Event|Object} event      native event, React synthetic event, or the
 *                                  EventCapture long-press synthetic object
 * @param {Object}       moreProps  chart moreProps (mouseXY fallback)
 * @param {Document}     targetDoc  document of the window the click landed in
 * @param {Element|null} container  the clicked chart's `.react-stockcharts`
 *                                  node (scopes the mouseXY fallback to the
 *                                  right panel in multi-chart layouts)
 */
export const normalizeShowEvent = (event, moreProps, targetDoc, container) => {
  const nativeEvent = getNativeEvent(event);
  let showEvent = nativeEvent;

  if (isTouchLike(nativeEvent)) {
    const touch = nativeEvent.changedTouches?.[0] || nativeEvent.touches?.[0];
    if (touch) {
      showEvent = {
        pageX: touch.pageX,
        pageY: touch.pageY,
        clientX: touch.clientX,
        clientY: touch.clientY,
        preventDefault: () => nativeEvent?.preventDefault?.(),
        stopPropagation: () => nativeEvent?.stopPropagation?.(),
      };
    }
  }

  // Fallback: chart-relative mouseXY → page coordinates via the container rect
  // (long-press synthetic events carry no coordinates at all).
  if (
    (!showEvent?.pageX && !showEvent?.pageY) ||
    showEvent.pageX === undefined
  ) {
    if (moreProps?.mouseXY && targetDoc) {
      const [x, y] = moreProps.mouseXY;
      const view = targetDoc.defaultView ?? window;
      const chartContainer =
        container ?? targetDoc.querySelector(".react-stockcharts");
      if (chartContainer) {
        const rect = chartContainer.getBoundingClientRect();
        showEvent = {
          pageX: rect.left + x + view.scrollX,
          pageY: rect.top + y + view.scrollY,
          clientX: rect.left + x,
          clientY: rect.top + y,
          preventDefault: () => {},
          stopPropagation: () => {},
        };
      }
    }
  }

  return showEvent?.pageX !== undefined ? showEvent : null;
};

/**
 * Merge a notification into the pending menu request for the SAME native
 * event, or start a new pending request for a new event.
 *
 * Slots (`interactive`, `indicator`, `moreProps`) are first-write-wins:
 * multiple chart sections broadcast the same right-click (see
 * InteractiveContextMenu's dedup comment), and the first call comes from the
 * section the user actually clicked.
 *
 * @returns {{ pending: Object, isNewEvent: boolean }}
 */
export const mergeContextPayload = (pending, nativeEvent, payload) => {
  if (pending && pending.nativeEvent === nativeEvent) {
    const merged = { ...pending };
    if (payload.interactive && !merged.interactive) {
      merged.interactive = payload.interactive;
    }
    if (payload.indicator && !merged.indicator) {
      merged.indicator = payload.indicator;
    }
    if (payload.moreProps && !merged.moreProps) {
      merged.moreProps = payload.moreProps;
    }
    return { pending: merged, isNewEvent: false };
  }

  return {
    pending: {
      nativeEvent,
      interactive: payload.interactive ?? null,
      indicator: payload.indicator ?? null,
      moreProps: payload.moreProps ?? null,
    },
    isNewEvent: true,
  };
};

/**
 * Keep a shown context-menu panel fully inside its window's viewport with a
 * minimum margin (UX rule: menus must never clip at the browser edge).
 * PrimeReact flips at edges but still lands flush (0px) — and the flip math
 * can overshoot with tall panels. Call on the menu's `onShow` (inside a rAF so
 * PrimeReact has finished positioning). Works on popout windows too: geometry
 * comes from the element's own window.
 *
 * @param {HTMLElement|null} el     the .p-contextmenu panel (ref.getElement())
 * @param {number}           margin minimum gap to every viewport edge (px)
 */
// Safe-area insets of the element's window (iOS notch/status bar/home bar —
// with viewport-fit=cover the page extends UNDER them, so a menu clamped to
// y=8 in portrait would sit beneath the status bar). Measured via a probe
// element because env() is CSS-only. Returns zeros when unmeasurable (tests,
// non-iOS).
const getSafeAreaInsets = (doc) => {
  const zero = { top: 0, right: 0, bottom: 0, left: 0 };
  if (!doc?.createElement || !doc.body) return zero;
  try {
    const probe = doc.createElement("div");
    probe.style.cssText =
      "position:fixed;visibility:hidden;pointer-events:none;" +
      "padding:env(safe-area-inset-top) env(safe-area-inset-right) " +
      "env(safe-area-inset-bottom) env(safe-area-inset-left)";
    doc.body.appendChild(probe);
    const cs = (doc.defaultView || window).getComputedStyle(probe);
    const inset = {
      top: parseFloat(cs.paddingTop) || 0,
      right: parseFloat(cs.paddingRight) || 0,
      bottom: parseFloat(cs.paddingBottom) || 0,
      left: parseFloat(cs.paddingLeft) || 0,
    };
    probe.remove();
    return inset;
  } catch {
    return zero;
  }
};

export const clampMenuToViewport = (el, margin = 8) => {
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  const win = el.ownerDocument?.defaultView || window;
  const rect = el.getBoundingClientRect();
  if (!rect.width && !rect.height) return; // hidden — nothing to clamp
  const inset = getSafeAreaInsets(el.ownerDocument);
  const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));
  const targetLeft = clamp(
    rect.left,
    margin + inset.left,
    win.innerWidth - rect.width - margin - inset.right,
  );
  const targetTop = clamp(
    rect.top,
    margin + inset.top,
    win.innerHeight - rect.height - margin - inset.bottom,
  );
  const dx = targetLeft - rect.left;
  const dy = targetTop - rect.top;
  if (!dx && !dy) return;
  // Shift the inline position PrimeReact set (absolute in the append target);
  // deltas keep it correct regardless of scroll/offset-parent specifics.
  if (dx)
    el.style.left = `${Math.round((parseFloat(el.style.left) || 0) + dx)}px`;
  if (dy)
    el.style.top = `${Math.round((parseFloat(el.style.top) || 0) + dy)}px`;
};
